/**
 * MJS Wishlist + Compare Engine v2.1
 * Custom built for My Jewellery Shop — Dawn Theme
 * localStorage-based, no third-party dependencies
 *
 * v2.1 Changes:
 * - Switched to EVENT DELEGATION for wishlist/compare buttons
 *   so dynamically rendered buttons (Quick View modal) work without re-binding
 * - Exposed refreshButtons() on global API for Quick View integration
 * - Fixed getPaymentOptions missing closing tag
 */

(function() {
  'use strict';

  const WISHLIST_KEY = 'mjs_wishlist';
  const COMPARE_KEY = 'mjs_compare';
  const COMPARE_MAX = 5;

  /* ============================================
     FORMATTING HELPERS
     ============================================ */

  function formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function parseMoneyToCents(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback || 0;
    if (typeof value === 'number') return Math.round(value);
    const normalised = String(value).replace(/[^0-9.-]/g, '');
    if (!normalised) return fallback || 0;
    const amount = parseFloat(normalised);
    if (Number.isNaN(amount)) return fallback || 0;
    return Math.round(amount * 100);
  }

  function getLiveVariant(item) {
    const variants = item && item.full && Array.isArray(item.full.variants) ? item.full.variants : [];
    return variants.find(v => v.available) || variants[0] || null;
  }

  function getLivePriceCents(item) {
    const variant = getLiveVariant(item);
    return variant ? parseMoneyToCents(variant.price, item.price || 0) : (item.price || 0);
  }

  function getLiveCompareAtCents(item) {
    const variant = getLiveVariant(item);
    return variant ? parseMoneyToCents(variant.compare_at_price, item.compare_at_price || 0) : (item.compare_at_price || 0);
  }

  function isLiveAvailable(item) {
    const variants = item && item.full && Array.isArray(item.full.variants) ? item.full.variants : [];
    return variants.length > 0 ? variants.some(v => v.available) : true;
  }

  function getProductDescription(product) {
    return product ? (product.body_html || product.description || '') : '';
  }

  function getProductImage(product, fallback) {
    if (!product) return fallback || '';
    if (Array.isArray(product.images) && product.images[0]) {
      return typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].src || fallback || '');
    }
    if (product.featured_image) return typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image.src || fallback || '');
    return fallback || '';
  }

  /* ============================================
     STORAGE HELPERS
     ============================================ */

  function getItems(key) {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveItems(key, items) {
    localStorage.setItem(key, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('mjs:storage-updated', { detail: { key } }));
  }

  /* ============================================
     WISHLIST CORE
     ============================================ */

  const Wishlist = {
    getAll() {
      return getItems(WISHLIST_KEY);
    },

    has(productId) {
      return this.getAll().some(item => item.id === productId);
    },

    add(product) {
      const items = this.getAll();
      if (items.some(item => item.id === product.id)) return false;
      product.added_at = new Date().toISOString();
      items.push(product);
      saveItems(WISHLIST_KEY, items);
      this.trackEvent('wishlist_add', product);
      return true;
    },

    remove(productId) {
      const items = this.getAll().filter(item => item.id !== productId);
      saveItems(WISHLIST_KEY, items);
      this.trackEvent('wishlist_remove', { id: productId });
      return true;
    },

    toggle(product) {
      if (this.has(product.id)) {
        this.remove(product.id);
        return false; // removed
      } else {
        this.add(product);
        return true; // added
      }
    },

    count() {
      return this.getAll().length;
    },

    clear() {
      saveItems(WISHLIST_KEY, []);
    },

    getShareUrl() {
      const items = this.getAll();
      const handles = items.map(i => i.handle).join(',');
      return window.location.origin + '/pages/wishlist?shared=' + encodeURIComponent(handles);
    },

    /* Klaviyo integration — track wishlist events */
    trackEvent(eventName, data) {
      if (typeof window._learnq !== 'undefined') {
        window._learnq.push(['track', eventName, {
          product_id: data.id,
          product_title: data.title || '',
          product_url: data.url || '',
          product_price: data.price || 0,
          product_image: data.image || ''
        }]);
      }
    }
  };

  /* ============================================
     COMPARE CORE
     ============================================ */

  const Compare = {
    getAll() {
      return getItems(COMPARE_KEY);
    },

    has(productId) {
      return this.getAll().some(item => item.id === productId);
    },

    add(product) {
      const items = this.getAll();
      if (items.length >= COMPARE_MAX) {
        document.dispatchEvent(new CustomEvent('mjs:compare-full'));
        return false;
      }
      if (items.some(item => item.id === product.id)) return false;
      product.added_at = new Date().toISOString();
      items.push(product);
      saveItems(COMPARE_KEY, items);
      return true;
    },

    remove(productId) {
      const items = this.getAll().filter(item => item.id !== productId);
      saveItems(COMPARE_KEY, items);
      return true;
    },

    toggle(product) {
      if (this.has(product.id)) {
        this.remove(product.id);
        return false;
      } else {
        return this.add(product);
      }
    },

    count() {
      return this.getAll().length;
    },

    clear() {
      saveItems(COMPARE_KEY, []);
    }
  };

  /* ============================================
     UI — WISHLIST BUTTONS (Heart Icons)
     Uses EVENT DELEGATION on document.body
     so dynamically injected buttons (Quick View) work
     ============================================ */

  function refreshWishlistButtonStates() {
    document.querySelectorAll('[data-mjs-wishlist-btn]').forEach(btn => {
      const productId = parseInt(btn.dataset.productId);
      if (!productId) return;
      const inList = Wishlist.has(productId);
      btn.classList.toggle('is-active', inList);
      btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
      /* Also toggle filled/outline SVG visibility for QV buttons */
      const outlineSvg = btn.querySelector('.mjs-wishlist-icon--outline');
      const filledSvg = btn.querySelector('.mjs-wishlist-icon--filled');
      if (outlineSvg) outlineSvg.style.display = inList ? 'none' : '';
      if (filledSvg) filledSvg.style.display = inList ? '' : 'none';
    });
  }

  function refreshCompareButtonStates() {
    document.querySelectorAll('[data-mjs-compare-btn]').forEach(btn => {
      const productId = parseInt(btn.dataset.productId);
      if (!productId) return;
      btn.classList.toggle('is-active', Compare.has(productId));
    });
  }

  /* Delegated wishlist click handler */
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-mjs-wishlist-btn]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const productId = parseInt(btn.dataset.productId);
    if (!productId) return;

    const product = {
      id: productId,
      handle: btn.dataset.productHandle || '',
      title: btn.dataset.productTitle || '',
      price: parseInt(btn.dataset.productPrice) || 0,
      compare_at_price: parseInt(btn.dataset.productComparePrice) || 0,
      image: btn.dataset.productImage || '',
      url: btn.dataset.productUrl || '',
      vendor: btn.dataset.productVendor || '',
      type: btn.dataset.productType || ''
    };

    const added = Wishlist.toggle(product);

    /* Update ALL wishlist buttons for this product (card + QV + PDP) */
    document.querySelectorAll('[data-mjs-wishlist-btn][data-product-id="' + productId + '"]').forEach(b => {
      b.classList.toggle('is-active', added);
      b.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
      const outlineSvg = b.querySelector('.mjs-wishlist-icon--outline');
      const filledSvg = b.querySelector('.mjs-wishlist-icon--filled');
      if (outlineSvg) outlineSvg.style.display = added ? 'none' : '';
      if (filledSvg) filledSvg.style.display = added ? '' : 'none';
    });

    /* Animate heart on the clicked button */
    btn.classList.add('mjs-wishlist-pulse');
    setTimeout(() => btn.classList.remove('mjs-wishlist-pulse'), 400);

    showToast(added ? 'Added to wishlist' : 'Removed from wishlist');
    updateWishlistCount();
  });

  /* Delegated compare click handler */
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-mjs-compare-btn]');
    if (!btn) return;

    /* Skip if this is inside the compare drawer or wishlist page actions */
    if (btn.closest('.mjs-compare-drawer') || btn.closest('.mjs-wishlist-card__actions')) return;

    e.preventDefault();
    e.stopPropagation();

    const productId = parseInt(btn.dataset.productId);
    if (!productId) return;

    const product = {
      id: productId,
      handle: btn.dataset.productHandle || '',
      title: btn.dataset.productTitle || '',
      price: parseInt(btn.dataset.productPrice) || 0,
      compare_at_price: parseInt(btn.dataset.productComparePrice) || 0,
      image: btn.dataset.productImage || '',
      url: btn.dataset.productUrl || '',
      vendor: btn.dataset.productVendor || '',
      type: btn.dataset.productType || ''
    };

    const added = Compare.toggle(product);

    /* Update ALL compare buttons for this product */
    document.querySelectorAll('[data-mjs-compare-btn][data-product-id="' + productId + '"]').forEach(b => {
      b.classList.toggle('is-active', added);
    });

    updateCompareBar();
    updateCompareCount();

    if (!added && Compare.count() === 0) {
      hideCompareBar();
    }
  });

  /* Listen for compare full event */
  document.addEventListener('mjs:compare-full', function() {
    showToast('Compare list is full (max 5 items)');
  });

  /* ============================================
     UI — HEADER WISHLIST INDICATOR
     ============================================ */

  function updateWishlistCount() {
    const count = Wishlist.count();
    document.querySelectorAll('[data-mjs-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  function updateCompareCount() {
    const count = Compare.count();
    document.querySelectorAll('[data-mjs-compare-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ============================================
     UI — COMPARE BAR (Sticky Bottom)
     ============================================ */

  function updateCompareBar() {
    const bar = document.getElementById('mjs-compare-bar');
    if (!bar) return;

    const items = Compare.getAll();
    const container = bar.querySelector('.mjs-compare-bar__items');
    if (!container) return;

    container.innerHTML = '';

    items.forEach(item => {
      const el = document.createElement('div');
      el.className = 'mjs-compare-bar__item';
      el.innerHTML = `
        <img src="${item.image}" alt="${item.title}" width="40" height="40">
        <span class="mjs-compare-bar__title">${item.title}</span>
        <button class="mjs-compare-bar__remove" data-remove-id="${item.id}" aria-label="Remove ${item.title}">\u2715</button>
      `;
      container.appendChild(el);
    });

    /* Add empty slots */
    for (let i = items.length; i < COMPARE_MAX; i++) {
      const el = document.createElement('div');
      el.className = 'mjs-compare-bar__item mjs-compare-bar__item--empty';
      el.innerHTML = '<span>+ Add item</span>';
      container.appendChild(el);
    }

    /* Show/hide bar */
    if (items.length > 0) {
      bar.classList.add('is-visible');
    } else {
      bar.classList.remove('is-visible');
    }

    /* Bind remove buttons */
    bar.querySelectorAll('.mjs-compare-bar__remove').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const removeId = parseInt(this.dataset.removeId);
        Compare.remove(removeId);
        updateCompareBar();
        updateCompareCount();
        /* Update card button state */
        document.querySelectorAll(`[data-mjs-compare-btn][data-product-id="${removeId}"]`).forEach(b => {
          b.classList.remove('is-active');
        });
      });
    });

    /* Compare now button */
    const compareBtn = bar.querySelector('.mjs-compare-bar__action');
    if (compareBtn) {
      compareBtn.disabled = items.length < 2;
    }
  }

  function hideCompareBar() {
    const bar = document.getElementById('mjs-compare-bar');
    if (bar) bar.classList.remove('is-visible');
  }

  /* ============================================
     UI — COMPARE DRAWER
     ============================================ */

  function openCompareDrawer() {
    const drawer = document.getElementById('mjs-compare-drawer');
    if (!drawer) return;

    const items = Compare.getAll();
    if (items.length < 2) {
      showToast('Add at least 2 items to compare');
      return;
    }

    const tbody = drawer.querySelector('.mjs-compare-table tbody');
    if (!tbody) return;

    /* Show loading state */
    tbody.innerHTML = '<tr><td colspan="' + (items.length + 1) + '" style="text-align:center;padding:40px;color:#999;">Loading detailed comparison...</td></tr>';
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    /* Fetch full product data from Shopify JSON API for each item */
    const fetches = items.map(item =>
      fetch('/products/' + item.handle + '.js')
        .then(r => r.json())
        .then(d => ({ ...item, full: d }))
        .catch(() => ({ ...item, full: null }))
    );

    Promise.all(fetches).then(enrichedItems => {

      /* Helper: extract tag value by prefix */
      function getTag(product, prefix) {
        if (!product || !product.tags) return null;
        const tags = typeof product.tags === 'string' ? product.tags.split(', ') : product.tags;
        const found = tags.find(t => t.toLowerCase().startsWith(prefix.toLowerCase()));
        return found ? found.split(':').slice(1).join(':').trim() : null;
      }

      /* Helper: extract from description */
      function extractFromDesc(product, pattern) {
        const description = getProductDescription(product);
        if (!description) return null;
        const text = description.replace(/<[^>]+>/g, ' ');
        const match = text.match(pattern);
        return match ? match[1].trim() : null;
      }

      /* Helper: get metal type from title, tags, or description */
      function getMetalType(item) {
        const p = item.full;
        const tag = getTag(p, 'metal:') || getTag(p, 'material:');
        if (tag) return tag;
        const title = (item.title || '').toLowerCase();
        if (title.includes('platinum')) return 'Platinum';
        if (title.includes('white gold')) return 'White Gold';
        if (title.includes('rose gold')) return 'Rose Gold';
        if (title.includes('yellow gold') || title.includes('yellow & white gold')) return title.includes('yellow & white') ? 'Yellow & White Gold' : 'Yellow Gold';
        if (title.includes('gold')) return 'Gold';
        if (title.includes('silver') || title.includes('sterling')) return 'Sterling Silver';
        const description = getProductDescription(p);
        if (description) {
          const desc = description.replace(/<[^>]+>/g, ' ');
          if (desc.match(/18ct.*yellow.*&.*white/i)) return '18ct Yellow & White Gold';
          if (desc.match(/18ct.*yellow.*gold/i)) return '18ct Yellow Gold';
          if (desc.match(/18ct.*white.*gold/i)) return '18ct White Gold';
          if (desc.match(/18ct.*rose.*gold/i)) return '18ct Rose Gold';
          if (desc.match(/9ct.*yellow.*gold/i)) return '9ct Yellow Gold';
          if (desc.match(/platinum/i)) return 'Platinum';
        }
        return 'See details';
      }

      /* Helper: get stone info */
      function getStoneInfo(item) {
        const p = item.full;
        const tag = getTag(p, 'stone:');
        if (tag) return tag;
        const title = (item.title || '').toLowerCase();
        if (title.includes('lab grown diamond')) return 'Lab Grown Diamond';
        if (title.includes('natural mined diamond') || title.includes('mined diamond')) return 'Natural Mined Diamond';
        if (title.includes('diamond')) return 'Diamond';
        if (title.includes('sapphire')) return 'Sapphire';
        if (title.includes('ruby')) return 'Ruby';
        if (title.includes('emerald')) return 'Emerald';
        if (title.includes('moissanite')) return 'Moissanite';
        if (title.includes('pearl')) return 'Pearl';
        return 'See details';
      }

      /* Helper: get carat weight from title or description */
      function getCaratWeight(item) {
        const titleMatch = (item.title || '').match(/(\d+\.?\d*)\s*ct/i);
        if (titleMatch) return titleMatch[1] + 'ct';
        const description = getProductDescription(item.full);
        if (description) {
          const desc = description.replace(/<[^>]+>/g, ' ');
          const descMatch = desc.match(/total.*?=\s*(\d+\.?\d*)\s*ct/i);
          if (descMatch) return descMatch[1] + 'ct (total)';
          const singleMatch = desc.match(/(\d+\.?\d*)\s*ct/i);
          if (singleMatch) return singleMatch[1] + 'ct';
        }
        return 'N/A';
      }

      /* Helper: get certification */
      function getCertification(item) {
        const p = item.full;
        if (!p) return 'N/A';
        const desc = getProductDescription(p).replace(/<[^>]+>/g, ' ');
        const certs = [];
        if (desc.match(/GIA/i)) certs.push('GIA');
        if (desc.match(/IGI/i)) certs.push('IGI');
        if (desc.match(/GCAL/i)) certs.push('GCAL');
        return certs.length > 0 ? certs.join(', ') + ' Certified' : 'See details';
      }

      /* Helper: get diamond specs */
      function getDiamondSpecs(item) {
        const p = item.full;
        const description = getProductDescription(p);
        if (!description) return '';
        const desc = description.replace(/<[^>]+>/g, ' ');
        const specs = [];
        const colorMatch = desc.match(/diamond\s+colour\s+([A-Z])/i);
        if (colorMatch) specs.push('Colour: ' + colorMatch[1]);
        const clarityMatch = desc.match(/diamond\s+clarity\s+(\w+)/i);
        if (clarityMatch) specs.push('Clarity: ' + clarityMatch[1]);
        const cutMatch = desc.match(/(round\s+brilliant|oval|pear|emerald|cushion|marquise|radiant|princess|asscher)/i);
        if (cutMatch) specs.push('Cut: ' + cutMatch[1]);
        return specs.length > 0 ? specs.join(' | ') : 'See details';
      }

      /* Helper: get available sizes */
      function getSizes(item) {
        const p = item.full;
        if (!p || !p.variants) return 'One size';
        const sizeOpt = (p.options || []).find(o => o.name.toLowerCase().includes('size'));
        if (sizeOpt) {
          return sizeOpt.values.join(', ');
        }
        if (p.variants.length === 1) return p.variants[0].title === 'Default Title' ? 'Contact for sizing' : p.variants[0].title;
        return p.variants.map(v => v.title).join(', ');
      }

      /* Helper: get variant options (metal color, etc) */
      function getVariantOptions(item) {
        const p = item.full;
        if (!p || !p.options) return 'N/A';
        return p.options.filter(o => o.name !== 'Title').map(o => '<strong>' + o.name + ':</strong> ' + o.values.join(', ')).join('<br>') || 'Default';
      }

      /* Helper: payment breakdown */
      function getPaymentOptions(item) {
        const priceCents = getLivePriceCents(item);
        const afterpay = formatMoney(Math.round(priceCents / 4));
        const zip = formatMoney(Math.round(priceCents / 60));
        return '<div class="mjs-compare-payments">' +
          '<div class="mjs-compare-payment-row"><strong>Afterpay</strong> 4 x ' + afterpay + '</div>' +
          '<div class="mjs-compare-payment-row"><strong>Zip Pay</strong> From ' + zip + '/week</div>' +
          '</div>';
      }

      /* Helper: educational "Best For" recommendation */
      function getBestFor(item) {
        const title = (item.title || '').toLowerCase();
        const type = (item.type || '').toLowerCase();
        const price = getLivePriceCents(item) / 100;
        const tips = [];

        if (title.includes('engagement') || type.includes('engagement')) {
          tips.push('Engagement');
        }
        if (title.includes('wedding') || type.includes('wedding')) {
          tips.push('Wedding');
        }
        if (title.includes('lab grown')) {
          tips.push('Eco-conscious buyers');
          tips.push('Larger stone for budget');
        }
        if (title.includes('natural mined')) {
          tips.push('Traditional value');
          tips.push('Investment piece');
        }
        if (title.includes('solitaire') || title.includes('1 x')) {
          tips.push('Classic, timeless style');
        }
        if (title.includes('halo')) {
          tips.push('Maximum sparkle');
        }
        if (title.includes('trilogy') || title.includes('three stone')) {
          tips.push('Symbolic (past, present, future)');
        }
        if (price > 8000) {
          tips.push('Premium investment');
        } else if (price < 3000) {
          tips.push('Great value');
        }

        if (tips.length === 0) tips.push('Everyday luxury');
        return '<div class="mjs-compare-bestfor">' + tips.map(t => '<span class="mjs-compare-bestfor-tag">' + t + '</span>').join(' ') + '</div>';
      }

      /* Build enhanced comparison rows */
      const rows = [
        { label: 'IMAGE', cls: 'mjs-compare-row--image', render: item => {
          const img = getProductImage(item.full, item.image);
          return '<a href="' + item.url + '"><img src="' + img + '" alt="' + item.title + '" class="mjs-compare-img"></a>' +
            '<button class="mjs-compare-remove-btn" data-remove-id="' + item.id + '" title="Remove">\u2715</button>';
        }},
        { label: 'PRODUCT', render: item => '<a href="' + item.url + '" class="mjs-compare-link">' + item.title + '</a>' },
        { label: 'PRICE', render: item => {
          const priceCents = getLivePriceCents(item);
          const compareAtCents = getLiveCompareAtCents(item);
          const priceStr = formatMoney(priceCents);
          if (compareAtCents > priceCents) {
            const cpStr = formatMoney(compareAtCents);
            return '<span class="mjs-compare-price--sale">' + priceStr + '</span> <span class="mjs-compare-price--was">' + cpStr + '</span>';
          }
          return '<span class="mjs-compare-price">' + priceStr + '</span>';
        }},
        { label: 'PAYMENT OPTIONS', render: item => getPaymentOptions(item) },
        { label: 'METAL TYPE', render: item => getMetalType(item) },
        { label: 'STONE TYPE', render: item => getStoneInfo(item) },
        { label: 'CARAT WEIGHT', render: item => getCaratWeight(item) },
        { label: 'DIAMOND SPECS', render: item => getDiamondSpecs(item) },
        { label: 'CERTIFICATION', render: item => getCertification(item) },
        { label: 'VARIANTS & OPTIONS', render: item => getVariantOptions(item) },
        { label: 'AVAILABLE SIZES', render: item => getSizes(item) },
        { label: 'CATEGORY', render: item => item.type || '\u2014' },
        { label: 'BRAND', render: item => item.vendor || 'My Jewellery Shop' },
        { label: 'AVAILABILITY', render: item => {
          return isLiveAvailable(item)
            ? '<span class="mjs-compare-instock"><span class="mjs-compare-stock-icon" aria-hidden="true">\u2713</span><span>In Stock</span></span>'
            : '<span class="mjs-compare-outstock"><span class="mjs-compare-stock-icon" aria-hidden="true">!</span><span>Out of Stock</span></span>';
        }},
        { label: 'BEST FOR', cls: 'mjs-compare-row--bestfor', render: item => getBestFor(item) },
        { label: 'ACTIONS', cls: 'mjs-compare-row--actions', render: item => {
          const avail = isLiveAvailable(item);
          const variant = getLiveVariant(item);
          const variantId = variant ? variant.id : '';
          const price = formatMoney(getLivePriceCents(item));
          return '<div class="mjs-compare-actions">' +
            '<a href="' + item.url + '" class="mjs-btn mjs-btn--primary mjs-btn--sm">View Full Details</a>' +
            (avail && variantId ? '<button class="mjs-btn mjs-btn--outline mjs-btn--sm mjs-compare-add-cart" data-variant-id="' + variantId + '">Add to Cart</button>' : '') +
            '<button class="mjs-btn mjs-btn--ghost mjs-btn--sm mjs-compare-add-wishlist" data-product-id="' + item.id + '">\u2661 Wishlist</button>' +
            '<button class="mjs-btn mjs-btn--ghost mjs-btn--sm mjs-hint-trigger" data-product-id="' + item.id + '" data-product-title="' + item.title + '" data-product-price="' + price + '" data-product-image="' + item.image + '" data-product-url="' + window.location.origin + item.url + '" data-product-vendor="' + (item.vendor || 'My Jewellery Shop') + '" data-hint-source="compare">\u2661 Drop a Hint</button>' +
          '</div>';
        }}
      ];

      tbody.innerHTML = '';
      rows.forEach(row => {
        const tr = document.createElement('tr');
        if (row.cls) tr.className = row.cls;
        tr.innerHTML = '<td class="mjs-compare-label">' + row.label + '</td>';
        enrichedItems.forEach(item => {
          const td = document.createElement('td');
          td.innerHTML = row.render(item);
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });

      /* Bind remove buttons inside drawer */
      tbody.querySelectorAll('.mjs-compare-remove-btn').forEach(btn => {
        btn.addEventListener('click', function() {
          const removeId = parseInt(this.dataset.removeId);
          Compare.remove(removeId);
          updateCompareCount();
          updateCompareBar();
          if (Compare.getAll().length < 2) {
            closeCompareDrawer();
          } else {
            openCompareDrawer();
          }
        });
      });

      /* Bind Add to Cart buttons */
      tbody.querySelectorAll('.mjs-compare-add-cart').forEach(btn => {
        btn.addEventListener('click', function() {
          const variantId = this.dataset.variantId;
          if (!variantId) {
            /* Fallback: navigate to product */
            const row = this.closest('td');
            const link = row ? row.querySelector('.mjs-compare-link, a[href*="/products/"]') : null;
            if (link) window.location.href = link.href;
            return;
          }
          this.textContent = 'Adding...';
          this.disabled = true;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
          })
          .then(r => r.json())
          .then((data) => {
            if (typeof window.mjsTrackAjaxAddToCart === 'function') {
              window.mjsTrackAjaxAddToCart({
                source: 'compare_drawer',
                variantId: variantId,
                quantity: 1,
                cartAddResponse: data
              });
            }
            this.textContent = 'Added!';
            this.style.background = '#2ecc71';
            this.style.color = '#fff';
            setTimeout(() => {
              this.textContent = 'Add to Cart';
              this.style.background = '';
              this.style.color = '';
              this.disabled = false;
              document.dispatchEvent(new CustomEvent('cart:updated'));
            }, 1500);
          })
          .catch(() => {
            this.textContent = 'Error';
            setTimeout(() => { this.textContent = 'Add to Cart'; this.disabled = false; }, 2000);
          });
        });
      });

      /* Bind Wishlist buttons */
      tbody.querySelectorAll('.mjs-compare-add-wishlist').forEach(btn => {
        btn.addEventListener('click', function() {
          const id = parseInt(this.dataset.productId);
          const item = enrichedItems.find(i => i.id === id);
          if (item) {
            Wishlist.add(item);
            updateWishlistCount();
            this.innerHTML = '\u2665 Added!';
            this.style.color = '#E53935';
          }
        });
      });
    });
  }

  function closeCompareDrawer() {
    const drawer = document.getElementById('mjs-compare-drawer');
    if (drawer) {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  /* ============================================
     UI — WISHLIST PAGE
     ============================================ */

  function initWishlistPage() {
    const container = document.getElementById('mjs-wishlist-grid');
    if (!container) return;

    /* Check for shared wishlist URL */
    const urlParams = new URLSearchParams(window.location.search);
    const sharedHandles = urlParams.get('shared');

    const items = Wishlist.getAll();
    const emptyState = document.getElementById('mjs-wishlist-empty');
    const filledState = document.getElementById('mjs-wishlist-filled');
    const countEl = document.getElementById('mjs-wishlist-page-count');

    if (items.length === 0 && !sharedHandles) {
      if (emptyState) emptyState.style.display = 'block';
      if (filledState) filledState.style.display = 'none';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (filledState) filledState.style.display = 'block';
    if (countEl) countEl.textContent = items.length + ' item' + (items.length !== 1 ? 's' : '');

    container.innerHTML = '';

    items.forEach(item => {
      const card = document.createElement('div');
      card.className = 'mjs-wishlist-card';
      const priceFormatted = formatMoney(item.price);
      let priceHtml = `<span class="mjs-wishlist-card__price">${priceFormatted}</span>`;
      if (item.compare_at_price > item.price) {
        const comparePriceFormatted = formatMoney(item.compare_at_price);
        priceHtml = `<span class="mjs-wishlist-card__price mjs-wishlist-card__price--sale">${priceFormatted}</span>
                     <span class="mjs-wishlist-card__price mjs-wishlist-card__price--was">${comparePriceFormatted}</span>`;
      }

      card.innerHTML = `
        <div class="mjs-wishlist-card__image">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </a>
          <button class="mjs-wishlist-card__remove" data-remove-id="${item.id}" aria-label="Remove from wishlist">\u2715</button>
        </div>
        <div class="mjs-wishlist-card__info">
          <h3 class="mjs-wishlist-card__title"><a href="${item.url}">${item.title}</a></h3>
          ${item.vendor ? `<p class="mjs-wishlist-card__vendor">${item.vendor}</p>` : ''}
          <div class="mjs-wishlist-card__prices">${priceHtml}</div>
          <div class="mjs-wishlist-card__actions">
            <a href="${item.url}" class="mjs-btn mjs-btn--primary">View Product</a>
            <button class="mjs-btn mjs-btn--outline mjs-wishlist-card__compare" data-mjs-compare-btn data-product-id="${item.id}" data-product-handle="${item.handle}" data-product-title="${item.title}" data-product-price="${item.price}" data-product-compare-price="${item.compare_at_price || 0}" data-product-image="${item.image}" data-product-url="${item.url}" data-product-vendor="${item.vendor || ''}" data-product-type="${item.type || ''}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/></svg>
              Compare
            </button>
            <button class="mjs-btn mjs-btn--outline mjs-wishlist-card__hint" data-hint-trigger data-product-id="${item.id}" data-product-title="${item.title}" data-product-price="${priceFormatted}" data-product-url="${item.url}" data-product-image="${item.image}" data-product-vendor="${item.vendor || ''}" data-hint-source="wishlist_item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              Drop a Hint
            </button>
          </div>
        </div>
      `;
      container.appendChild(card);
    });

    /* Bind remove buttons */
    container.querySelectorAll('.mjs-wishlist-card__remove').forEach(btn => {
      btn.addEventListener('click', function() {
        const removeId = parseInt(this.dataset.removeId);
        Wishlist.remove(removeId);
        initWishlistPage(); // re-render
        updateWishlistCount();
      });
    });

    /* Share wishlist button */
    const shareBtn = document.getElementById('mjs-wishlist-share');
    if (shareBtn) {
      shareBtn.addEventListener('click', function() {
        const url = Wishlist.getShareUrl();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => {
            showToast('Wishlist link copied!');
          });
        } else {
          prompt('Copy this link to share your wishlist:', url);
        }
      });
    }
  }

  /* ============================================
     UI — DROP A HINT MODAL (connects to Drop Hint 2.0)
     ============================================ */

  function openDropHintModal(product) {
    /* If Drop Hint 2.0 app is installed, trigger its modal */
    if (typeof window.DropHint !== 'undefined') {
      window.DropHint.open(product);
      return;
    }

    /* Fallback: simple email hint form */
    const modal = document.getElementById('mjs-hint-modal');
    if (!modal) return;

    modal.querySelector('.mjs-hint-product-title').textContent = product.title;
    if (modal.querySelector('.mjs-hint-product-image')) {
      modal.querySelector('.mjs-hint-product-image').src = product.image;
    }
    if (modal.querySelector('.mjs-hint-product-url')) {
      modal.querySelector('.mjs-hint-product-url').value = product.url;
    }

    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  /* ============================================
     UI — TOAST NOTIFICATIONS
     ============================================ */

  function showToast(message) {
    let toast = document.getElementById('mjs-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'mjs-toast';
      toast.className = 'mjs-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2500);
  }

  /* ============================================
     GLOBAL EVENT LISTENERS
     ============================================ */

  function initGlobalListeners() {
    /* Compare bar — Compare Now button */
    document.addEventListener('click', function(e) {
      if (e.target.closest('.mjs-compare-bar__action')) {
        openCompareDrawer();
      }
      if (e.target.closest('.mjs-compare-bar__clear')) {
        Compare.clear();
        updateCompareBar();
        updateCompareCount();
        document.querySelectorAll('[data-mjs-compare-btn]').forEach(b => b.classList.remove('is-active'));
      }
      if (e.target.closest('.mjs-compare-drawer__close') || e.target.closest('.mjs-compare-drawer__overlay')) {
        closeCompareDrawer();
      }
      /* Drop a Hint trigger — from compare drawer or wishlist */
      if (e.target.closest('.mjs-hint-trigger') || e.target.closest('[data-hint-trigger]')) {
        const btn = e.target.closest('.mjs-hint-trigger') || e.target.closest('[data-hint-trigger]');
        const product = {
          id: btn.dataset.productId || '',
          title: btn.dataset.productTitle || '',
          price: btn.dataset.productPrice || '',
          image: btn.dataset.productImage || '',
          url: btn.dataset.productUrl || '',
          vendor: btn.dataset.productVendor || 'My Jewellery Shop'
        };
        openDropHintModal(product);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.target.closest('.mjs-hint-modal__close') || e.target.closest('.mjs-hint-modal__overlay')) {
        const modal = document.getElementById('mjs-hint-modal');
        if (modal) {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      }
    });

    /* Listen for storage updates to sync across tabs */
    window.addEventListener('storage', function(e) {
      if (e.key === WISHLIST_KEY || e.key === COMPARE_KEY) {
        updateWishlistCount();
        updateCompareCount();
        updateCompareBar();
        refreshWishlistButtonStates();
        refreshCompareButtonStates();
      }
    });
  }

  /* ============================================
     INITIALIZATION
     ============================================ */

  function init() {
    refreshWishlistButtonStates();
    refreshCompareButtonStates();
    updateWishlistCount();
    updateCompareCount();
    updateCompareBar();
    initWishlistPage();
    initGlobalListeners();
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init after Shopify section renders (for AJAX pagination etc.) */
  document.addEventListener('shopify:section:load', init);

  /* Expose API globally for other scripts/apps */
  window.MJSWishlist = Wishlist;
  window.MJSCompare = Compare;

  /* Expose refreshButtons for Quick View integration */
  window.MJSWishlist.refreshButtons = refreshWishlistButtonStates;
  window.MJSCompare.refreshButtons = refreshCompareButtonStates;

})();
